import React, { useCallback, useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import { deleteSessionItem, getSessionItem, setSessionItem } from '@atlassian/search-dialog/session-storage-utils';

import { PROMPT_OVERRIDE_SESSION_KEY } from '../../constants';

import { messages } from './messages';

export type PromptOverrideModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

export function PromptOverrideModal({
	isOpen,
	onClose,
}: PromptOverrideModalProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [promptText, setPromptText] = useState('');

	useEffect(() => {
		if (isOpen) {
			const stored = getSessionItem(PROMPT_OVERRIDE_SESSION_KEY);
			setPromptText(stored || '');
		}
	}, [isOpen]);

	const handleSave = useCallback(() => {
		const trimmedPrompt = promptText.trim();

		if (trimmedPrompt) {
			setSessionItem(PROMPT_OVERRIDE_SESSION_KEY, trimmedPrompt);
		} else {
			deleteSessionItem(PROMPT_OVERRIDE_SESSION_KEY);
		}

		onClose();
	}, [promptText, onClose]);

	const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setPromptText(e.target.value);
	}, []);

	return (
		<ModalTransition>
			{isOpen && (
				<Modal onClose={onClose} width="large" testId="prompt-override-modal">
					<ModalHeader hasCloseButton>
						<ModalTitle>
							<FormattedMessage {...messages.prompt_override_modal_title} />
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<Stack space="space.200">
							<Box>
								<Stack space="space.100">
									<Text weight="semibold">
										<FormattedMessage {...messages.prompt_override_custom_prompt_label} />
									</Text>
									<TextArea
										value={promptText}
										onChange={handleTextChange}
										placeholder={formatMessage(messages.prompt_override_textarea_placeholder)}
										testId="prompt-override-textarea"
										resize="vertical"
									/>
									<Inline space="space.100" spread="space-between">
										<Text size="small" color="color.text.subtlest">
											<FormattedMessage {...messages.prompt_override_help_text} />
										</Text>
									</Inline>
								</Stack>
							</Box>
						</Stack>
					</ModalBody>
					<ModalFooter>
						<Inline space="space.100">
							<Button appearance="subtle" onClick={onClose}>
								<FormattedMessage {...messages.prompt_override_cancel_button} />
							</Button>
							<Button
								appearance="primary"
								onClick={handleSave}
								testId="prompt-override-save-button"
							>
								<FormattedMessage {...messages.prompt_override_save_button} />
							</Button>
						</Inline>
					</ModalFooter>
				</Modal>
			)}
		</ModalTransition>
	);
}
